(() => {
  'use strict';

  class TFCollectionProducts extends HTMLElement {
    constructor() {
      super();

      this.isLoading = false;
      this.requestToken = 0;
      this.viewState = null;
      this.infiniteObserver = null;
      this.infiniteCanLoad = true;
      this.infiniteScrollStart = 0;
      this.lastInfiniteUrl = '';
      this.scrollFrame = null;
      this.resizeFrame = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.paginationType = this.dataset.paginationType || 'numbered';
      this.infiniteScrollStart = window.scrollY;

      this.abortController?.abort();
      this.abortController = new AbortController();

      this.addEventListener('click', this.handleClick, {
        signal: this.abortController.signal
      });

      this.addEventListener('change', this.handleChange, {
        signal: this.abortController.signal
      });

      window.addEventListener('popstate', this.handlePopState, {
        signal: this.abortController.signal
      });

      window.addEventListener('scroll', this.handleScroll, {
        passive: true,
        signal: this.abortController.signal
      });

      window.addEventListener('resize', this.handleResize, {
        passive: true,
        signal: this.abortController.signal
      });

      this.refreshElements();
      this.restoreView();

      window.requestAnimationFrame(() => {
        if (this.isConnected) {
          this.setupInfinitePagination();
        }
      });

      this.setAttribute('data-js-ready', '');
    }

    disconnectedCallback() {
        this.abortController?.abort();
        this.requestController?.abort();
        this.infiniteObserver?.disconnect();

        if (this.scrollFrame) {
          window.cancelAnimationFrame(this.scrollFrame);
        }

        if (this.resizeFrame) {
          window.cancelAnimationFrame(this.resizeFrame);
        }
    }

    refreshElements() {
      this.grid = this.querySelector('[data-product-grid]');
      this.toolbar = this.querySelector('[data-collection-toolbar]');
      this.activeFilters = this.querySelector('[data-active-filters]');
      this.paginationSlot = this.querySelector('[data-pagination-slot]');
      this.loadingOverlay = this.querySelector('[data-loading-overlay]');
      this.statusElement = this.querySelector('[data-collection-status]');
      this.viewButtons = [...this.querySelectorAll('[data-view-button]')];
      this.loadMoreLink = this.querySelector('[data-load-more-link]');
      this.loadMoreSpinner = this.querySelector('[data-load-more-spinner]');
      this.infiniteSentinel = this.querySelector('[data-infinite-sentinel]');

      this.sizeSortSelect(this.querySelector('[data-sort-select]'));
    }

    // Native <select> elements size their closed box to the widest <option>
    // in the list, not the one currently selected — so "Featured" still
    // renders as wide as "Alphabetically, A-Z". Measure the selected
    // option's own text and feed it back as a CSS variable so the box
    // shrinks to match what's actually showing.
    sizeSortSelect(select) {
      if (!select) {
        return;
      }

      const selectedOption = select.options[select.selectedIndex];

      if (!selectedOption) {
        return;
      }

      const computed = window.getComputedStyle(select);
      const measureSpan = document.createElement('span');

      measureSpan.style.cssText =
        'position:absolute; visibility:hidden; white-space:nowrap; top:-9999px; left:-9999px;';
      measureSpan.style.font = computed.font;
      measureSpan.textContent = selectedOption.text;

      document.body.appendChild(measureSpan);
      const textWidth = measureSpan.getBoundingClientRect().width;
      measureSpan.remove();

      const paddingInlineStart = Number.parseFloat(computed.paddingInlineStart) || 0;
      const paddingInlineEnd = Number.parseFloat(computed.paddingInlineEnd) || 0;
      const borderWidth =
        (Number.parseFloat(computed.borderLeftWidth) || 0) +
        (Number.parseFloat(computed.borderRightWidth) || 0);

      const totalWidth = Math.ceil(
        textWidth + paddingInlineStart + paddingInlineEnd + borderWidth
      );

      select.style.setProperty('--sort-select-width', `${totalWidth}px`);
    }
    handleResize() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;

        if (!this.isConnected || !this.viewState) {
          return;
        }

        this.applyView(this.viewState);
      });
    }

    getAvailableGridColumns() {
      return this.viewButtons
        .filter((button) => {
          return button.dataset.viewMode !== 'list';
        })
        .map((button) => {
          return Number.parseInt(
            button.dataset.viewColumns,
            10
          );
        })
        .filter(Number.isFinite);
    }

    getResponsiveMaxColumns() {
      const availableColumns =
        this.getAvailableGridColumns();

      const maximumAvailable =
        availableColumns.length > 0
          ? Math.max(...availableColumns)
          : 4;

      if (window.innerWidth < 750) {
        return Math.min(2, maximumAvailable);
      }

      if (window.innerWidth < 1200) {
        return Math.min(3, maximumAvailable);
      }

      return maximumAvailable;
    }

    updateViewButtons(
      mode,
      activeColumns,
      maximumColumns
    ) {
      this.viewButtons.forEach((button) => {
        const buttonMode =
          button.dataset.viewMode === 'list'
            ? 'list'
            : 'grid';

        const buttonColumns =
          Number.parseInt(
            button.dataset.viewColumns,
            10
          ) || 1;

        const shouldHide =
          buttonMode === 'grid' &&
          buttonColumns > maximumColumns;

        button.hidden = shouldHide;

        const isActive =
          !shouldHide &&
          buttonMode === mode &&
          (
            mode === 'list' ||
            buttonColumns === activeColumns
          );

        button.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );

        button.classList.toggle(
          'is-active',
          isActive
        );
      });
    }


    handleChange(event) {
      const select = event.target.closest('[data-sort-select]');

      if (!select || !this.contains(select)) {
        return;
      }

      this.sizeSortSelect(select);

      const url = new URL(window.location.href);

      if (select.value) {
        url.searchParams.set('sort_by', select.value);
      } else {
        url.searchParams.delete('sort_by');
      }

      url.searchParams.delete('page');

      this.updateCollection(url, {
        mode: 'replace',
        historyMode: 'push',
        shouldFocus: true
      });
    }

    handleClick(event) {
      const viewButton = event.target.closest('[data-view-button]');

      if (viewButton && this.contains(viewButton)) {
        event.preventDefault();

        this.applyView({
          mode: viewButton.dataset.viewMode,
          columns: Number.parseInt(
            viewButton.dataset.viewColumns,
            10
          )
        });

        return;
      }

      const loadMoreLink = event.target.closest('[data-load-more-link]');

      if (loadMoreLink && this.contains(loadMoreLink)) {
        event.preventDefault();

        this.updateCollection(loadMoreLink.href, {
          mode: 'append',
          historyMode: 'none',
          shouldFocus: false
        });

        return;
      }

      const paginationLink = event.target.closest('[data-pagination-link]');

      if (paginationLink && this.contains(paginationLink)) {
        event.preventDefault();

        this.updateCollection(paginationLink.href, {
          mode: 'replace',
          historyMode: 'push',
          shouldFocus: true
        });

        return;
      }

      const updateLink = event.target.closest('[data-collection-update]');

      if (updateLink && this.contains(updateLink)) {
        event.preventDefault();

        this.updateCollection(updateLink.href, {
          mode: 'replace',
          historyMode: 'push',
          shouldFocus: true
        });
      }
    }

    handlePopState() {
      this.updateCollection(window.location.href, {
        mode: 'replace',
        historyMode: 'none',
        shouldFocus: false
      });
    }

    handleScroll() {
      if (
        this.paginationType !== 'infinite' ||
        this.isLoading ||
        this.infiniteCanLoad
      ) {
        return;
      }

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;

        if (
          window.scrollY >
          this.infiniteScrollStart + 24
        ) {
          this.infiniteCanLoad = true;
          this.checkInfinitePosition();
        }
      });
    }

    restoreView() {
      const defaultMode =
        this.dataset.defaultView === 'list'
          ? 'list'
          : 'grid';

      const defaultColumns =
        Number.parseInt(
          this.dataset.defaultColumns,
          10
        ) || 4;

      this.applyView({
        mode: defaultMode,
        columns: defaultColumns
      });
    }

    applyView(view) {
      const mode =
        view?.mode === 'list'
          ? 'list'
          : 'grid';

      const requestedColumns =
        mode === 'list'
          ? 1
          : Math.min(
              Math.max(
                Number.parseInt(
                  view?.columns,
                  10
                ) || 4,
                2
              ),
              6
            );

      this.viewState = {
        mode,
        columns: requestedColumns
      };

      const maximumColumns =
        this.getResponsiveMaxColumns();

      const activeColumns =
        mode === 'list'
          ? 1
          : Math.min(
              requestedColumns,
              maximumColumns
            );

      if (this.grid) {
        this.grid.dataset.view = mode;

        this.grid.style.setProperty(
          '--view-columns-current',
          String(activeColumns)
        );

        this.grid.style.setProperty(
          '--view-columns-desktop',
          String(activeColumns)
        );
      }

      this.updateViewButtons(
        mode,
        activeColumns,
        maximumColumns
      );
    }

    async updateCollection(
      url,
      {
        mode = 'replace',
        historyMode = 'none',
        shouldFocus = false
      } = {}
    ) {
      if (
        !this.sectionId ||
        this.isLoading
      ) {
        return;
      }

      const token = ++this.requestToken;

      this.requestController?.abort();
      this.requestController =
        new AbortController();

      this.setLoading(true, mode);

      const browserUrl = new URL(
        url,
        window.location.origin
      );

      browserUrl.searchParams.delete(
        'section_id'
      );

      const sectionUrl =
        new URL(browserUrl);

      sectionUrl.searchParams.set(
        'section_id',
        this.sectionId
      );

      try {
        const response = await fetch(
          sectionUrl,
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              Accept: 'text/html',
              'X-Requested-With':
                'XMLHttpRequest'
            },
            signal:
              this.requestController.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            `Collection request failed: ${response.status}`
          );
        }

        const html =
          await response.text();

        const documentResult =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        const nextComponent =
          documentResult.querySelector(
            `tf-collection-products[data-section-id="${this.escapeSelector(
              this.sectionId
            )}"]`
          );

        if (!nextComponent) {
          throw new Error(
            'Invalid collection section response.'
          );
        }

        if (
          token !== this.requestToken ||
          !this.isConnected
        ) {
          return;
        }

        if (mode === 'append') {
          this.appendProducts(
            nextComponent
          );
        } else {
          this.replaceResults(
            nextComponent
          );
        }

        if (historyMode === 'push') {
          window.history.pushState(
            {},
            '',
            browserUrl
          );
        } else if (
          historyMode === 'replace'
        ) {
          window.history.replaceState(
            {},
            '',
            browserUrl
          );
        }

        this.announce(
          this.dataset.loadedText ||
            'Products updated.'
        );

        document.dispatchEvent(
          new CustomEvent(
            'collection:updated',
            {
              detail: {
                sectionId: this.sectionId,
                mode,
                url: browserUrl.toString()
              }
            }
          )
        );

        if (shouldFocus) {
          this.focusResults();
        }
      } catch (error) {
        if (
          error.name === 'AbortError'
        ) {
          return;
        }

        if (
          mode === 'append' &&
          this.paginationType === 'infinite'
        ) {
          this.lastInfiniteUrl = '';
          this.infiniteCanLoad = true;
        }

        console.error(
          '[Collection products]',
          error
        );

        this.announce(
          this.dataset.errorText ||
            'Products could not be loaded.'
        );
      } finally {
        if (
          token !== this.requestToken
        ) {
          return;
        }

        this.setLoading(false, mode);

        if (
          this.paginationType ===
          'infinite'
        ) {
          window.requestAnimationFrame(
            () => {
              if (
                this.isConnected &&
                !this.isLoading
              ) {
                this.setupInfinitePagination();
              }
            }
          );
        }
      }
    }

    replaceResults(nextComponent) {
      const currentView =
        this.viewState;

      this.infiniteObserver?.disconnect();
      this.infiniteCanLoad = true;
      this.infiniteScrollStart =
        window.scrollY;
      this.lastInfiniteUrl = '';

      const openDrawer = this.querySelector(
        'facet-drawer:not([hidden])'
      );

      this.innerHTML =
        nextComponent.innerHTML;

      // An open drawer swapped in wholesale would reset to its closed
      // markup and flash shut - keep the same (open) element in place and
      // only swap its facet-form for the freshly fetched one.
      if (openDrawer) {
        const freshDrawer = this.querySelector('facet-drawer');
        const openForm = openDrawer.querySelector('facet-form');
        const freshForm = freshDrawer?.querySelector('facet-form');

        if (freshDrawer && openForm && freshForm) {
          openForm.replaceWith(freshForm);
          freshDrawer.replaceWith(openDrawer);
        }
      }

      this.refreshElements();

      if (currentView) {
        this.applyView(currentView);
      } else {
        this.restoreView();
      }

      window.customElements?.upgrade?.(
        this
      );
    }

    appendProducts(nextComponent) {
      const nextGrid =
        nextComponent.querySelector(
          '[data-product-grid]'
        );

      const nextPaginationSlot =
        nextComponent.querySelector(
          '[data-pagination-slot]'
        );

      if (
        !this.grid ||
        !nextGrid
      ) {
        throw new Error(
          'Next collection page has no product grid.'
        );
      }

      const fragment =
        document.createDocumentFragment();

      [...nextGrid.children].forEach(
        (item) => {
          fragment.appendChild(item);
        }
      );

      this.grid.appendChild(fragment);

      if (
        this.paginationSlot &&
        nextPaginationSlot
      ) {
        this.paginationSlot.innerHTML =
          nextPaginationSlot.innerHTML;
      }

      this.refreshElements();

      if (this.viewState) {
        this.applyView(
          this.viewState
        );
      }

      window.customElements?.upgrade?.(
        this.grid
      );
    }

    setupInfinitePagination() {
      this.infiniteObserver?.disconnect();
      this.infiniteObserver = null;

      if (
        this.paginationType !== 'infinite'
      ) {
        return;
      }

      const sentinel =
        this.infiniteSentinel ||
        this.querySelector(
          '[data-infinite-sentinel]'
        );

      const link =
        this.loadMoreLink ||
        this.querySelector(
          '[data-load-more-link]'
        );

      if (
        !sentinel ||
        !link
      ) {
        return;
      }

      if (
        !(
          'IntersectionObserver'
          in window
        )
      ) {
        link.hidden = false;
        return;
      }

      link.hidden = true;

      this.infiniteObserver =
        new IntersectionObserver(
          (entries) => {
            const entry =
              entries[0];

            if (!entry) {
              return;
            }

            if (
              !entry.isIntersecting
            ) {
              return;
            }

            this.triggerInfiniteLoad(
              link.href
            );
          },
          {
            root: null,
            rootMargin:
              '0px 0px 80px 0px',
            threshold: 0
          }
        );

      this.infiniteObserver.observe(
        sentinel
      );
    }

    checkInfinitePosition() {
      if (
        this.paginationType !== 'infinite' ||
        this.isLoading ||
        !this.infiniteCanLoad
      ) {
        return;
      }

      const sentinel =
        this.infiniteSentinel ||
        this.querySelector(
          '[data-infinite-sentinel]'
        );

      const link =
        this.loadMoreLink ||
        this.querySelector(
          '[data-load-more-link]'
        );

      if (
        !sentinel ||
        !link
      ) {
        return;
      }

      const rect =
        sentinel.getBoundingClientRect();

      if (
        rect.top <=
          window.innerHeight + 80 &&
        rect.bottom >= 0
      ) {
        this.triggerInfiniteLoad(
          link.href
        );
      }
    }

    triggerInfiniteLoad(url) {
      if (
        !url ||
        this.isLoading ||
        !this.infiniteCanLoad ||
        this.lastInfiniteUrl === url
      ) {
        return;
      }

      this.infiniteCanLoad = false;
      this.infiniteScrollStart =
        window.scrollY;
      this.lastInfiniteUrl = url;

      this.infiniteObserver?.disconnect();

      this.updateCollection(url, {
        mode: 'append',
        historyMode: 'none',
        shouldFocus: false
      });
    }

    setLoading(
      isLoading,
      mode
    ) {
      this.isLoading = isLoading;

      this.classList.toggle(
        'is-loading',
        isLoading
      );

      this.classList.toggle(
        'is-loading-more',
        isLoading &&
          mode === 'append'
      );

      this.toggleAttribute(
        'aria-busy',
        isLoading
      );

      this.grid?.setAttribute(
        'aria-busy',
        isLoading
          ? 'true'
          : 'false'
      );

      if (this.loadingOverlay) {
        this.loadingOverlay.hidden =
          !isLoading ||
          mode === 'append';
      }

      if (this.loadMoreLink) {
        this.loadMoreLink.setAttribute(
          'aria-disabled',
          isLoading
            ? 'true'
            : 'false'
        );

        this.loadMoreLink.classList.toggle(
          'is-loading',
          isLoading
        );
      }

      if (this.loadMoreSpinner) {
        this.loadMoreSpinner.hidden =
          !isLoading;
      }

      if (isLoading) {
        this.announce(
          this.dataset.loadingText ||
            'Loading products.'
        );
      }
    }

    focusResults() {
      if (!this.grid) {
        return;
      }

      this.grid.focus({
        preventScroll: true
      });

      // Once the sticky toolbar is "stuck" it renders on top of whatever
      // scrolls up underneath it, so scrollIntoView on either the toolbar
      // or the grid leaves the grid's first row (or the active-filter chip
      // row sitting between the two) hidden behind it. The chip row and
      // grid are ordinary flow elements though - sticky positioning never
      // changes their own layout, only the toolbar's - so anchoring on the
      // chip row's (or grid's) own natural position already bakes in
      // whatever margins sit between it and the toolbar, without having to
      // add up individual heights and gaps by hand. Reserve just the
      // toolbar's height plus the sticky site header's own offset
      // (--sticky-header-offset, kept live by header.js) above that anchor.
      const computeTarget = () => {
        const stickyHeaderOffset =
          Number.parseFloat(
            getComputedStyle(
              document.documentElement
            ).getPropertyValue('--sticky-header-offset')
          ) || 0;

        const toolbarHeight = this.toolbar
          ? this.toolbar.getBoundingClientRect().height
          : 0;

        const anchor =
          this.activeFilters && this.activeFilters.isConnected
            ? this.activeFilters
            : this.grid;

        return Math.max(
          anchor.getBoundingClientRect().top +
            window.scrollY -
            stickyHeaderOffset -
            toolbarHeight,
          0
        );
      };

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      window.scrollTo({
        top: computeTarget(),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });

      // Scrolling up can itself reveal the sticky header (header.js's
      // scroll-up-to-reveal mode), which only updates --sticky-header-offset
      // once that scroll is under way - after the animation settles,
      // recompute against the now-current values and snap-correct any
      // mismatch that introduced.
      const settle = () => {
        window.scrollTo({
          top: computeTarget(),
          behavior: 'auto'
        });
      };

      if ('onscrollend' in window) {
        window.addEventListener('scrollend', settle, {
          once: true
        });
      } else {
        window.setTimeout(settle, reducedMotion ? 50 : 500);
      }
    }

    announce(message) {
      if (
        !message ||
        !this.statusElement
      ) {
        return;
      }

      this.statusElement.textContent = '';

      window.requestAnimationFrame(
        () => {
          this.statusElement.textContent =
            message;
        }
      );
    }

    escapeSelector(value) {
      if (window.CSS?.escape) {
        return window.CSS.escape(
          String(value)
        );
      }

      return String(value).replace(
        /["\\]/g,
        '\\$&'
      );
    }
  }

  if (
    !customElements.get(
      'tf-collection-products'
    )
  ) {
    customElements.define(
      'tf-collection-products',
      TFCollectionProducts
    );
  }
})();
